import { useState } from 'react'
import { api, type EvaluationDetail, type VerdictCompliance, type VerdictRow, type VerdictState } from '../api'
import { OUTCOME_BADGE, OUTCOME_LABEL } from '../agreementWords'
import { benchmarkSays } from '../benchmarkWords'
import { says } from '../limits'
import { ComplianceReviewPanel } from './ComplianceReview'
import { RowMenu } from './RowMenu'
import { amount, asAt, covers, room, rowName, why } from '../verdictWords'

/**
 * What one evaluation run came to, limit by limit — §6.4, proposal §5.5.
 *
 * The counts say how many; this says which, and why. Every verdict carries what it
 * judged (the holdings the limit covers), what it found against the limit, how much
 * room is left, and in the case of a warning or a breach the reason in words. A
 * limit that could not be judged says what would decide it, which is usually a
 * column to map when the snapshot is loaded. A limit on each issuer or each country
 * lists the ones nearest their cap, because *3.4%* is a number and *ACME at 3.4%* is
 * something to act on.
 *
 * Every verdict says the day the holdings were as at, because it is about that day
 * and not about today. The words come from `verdictWords.ts`, which the Excel pane
 * shares and the exported file is held to.
 *
 * Limits written as words are listed apart and last: a person reads them, and no
 * holding was ever going to be checked against them.
 */

const STATE_LABEL: Record<VerdictState, string> = {
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
  warn: 'Warn',
  pass: 'Pass',
}

const SHOWN_GROUPS = 5

/** What a limit's words name in the snapshot: each term, and the largest holdings it found. */
function Found({ review }: { review: VerdictRow['review'] }) {
  if (!review || review.terms.length === 0) {
    return <span className="clause">No holding matches its words.</span>
  }
  const percent = (value: number | null) => (value === null ? '' : `, ${value.toFixed(2)}% of NAV`)
  return (
    <>
      {review.terms.map((t) => (
        <span key={t.term} className="found-term">
          <strong>{t.term}</strong>: {t.holdings} holding{t.holdings === 1 ? '' : 's'}
          {percent(t.pct_of_nav)}
        </span>
      ))}
      <ul className="verdict-groups">
        {review.positions.slice(0, SHOWN_GROUPS).map((p) => (
          <li key={p.line}>
            {p.security_id}
            {p.issuer_id ? ` (${p.issuer_id})` : ''}
            {p.pct_of_nav === null ? '' : `: ${p.pct_of_nav.toFixed(2)}%`}
          </li>
        ))}
        {review.matched_holdings > SHOWN_GROUPS && <li>and {review.matched_holdings - SHOWN_GROUPS} more</li>}
      </ul>
    </>
  )
}

/** Where a verdict stands with compliance, beside the verdict (decision 53). */
function ComplianceCell({ compliance }: { compliance: VerdictCompliance | undefined }) {
  if (!compliance) return null
  return (
    <span className={`verdict-count ${OUTCOME_BADGE[compliance.outcome]}`} data-testid="verdict-compliance">
      {OUTCOME_LABEL[compliance.outcome]}
    </span>
  )
}

/** `onClose` where the verdicts sit below something to return to; the Verdicts
 *  screen opens them in place of its list and has its own way back. `canReview` offers
 *  compliance's review of each verdict, which everyone else can read. */
export function RunVerdicts({
  detail,
  onClose,
  canReview = false,
}: {
  detail: EvaluationDetail
  onClose?: () => void
  canReview?: boolean
}) {
  const judged = detail.verdicts.filter((v) => v.template !== 'narrative')
  const words = detail.verdicts.filter((v) => v.template === 'narrative')
  const day = asAt(detail.snapshot_as_of)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The verdict whose Compliance review is open in place of the list. */
  const [reviewing, setReviewing] = useState<string | null>(null)
  /** Reviews saved here since the run was fetched. */
  const [saved, setSaved] = useState<Record<string, VerdictCompliance>>({})
  const reviewed = detail.verdicts.some((v) => v.compliance !== undefined)
  const complianceOf = (v: VerdictRow) => saved[v.rule_id] ?? v.compliance
  const ordered = [...judged, ...words]

  function reviewMenu(v: VerdictRow) {
    return (
      <RowMenu
        label={`Actions for ${rowName(v.rule_key)}`}
        items={[{ label: 'Compliance review', onSelect: () => setReviewing(v.rule_id) }]}
      />
    )
  }

  const open = ordered.findIndex((v) => v.rule_id === reviewing)
  if (open >= 0) {
    const v = ordered[open]!
    const previous = ordered[open - 1]
    const next = ordered[open + 1]
    return (
      <section className="run-verdicts" data-testid="run-verdicts">
        <ComplianceReviewPanel
          key={v.rule_id}
          detail={detail}
          verdict={v}
          compliance={complianceOf(v)}
          canReview={canReview}
          position={`${open + 1} of ${ordered.length}`}
          onPrevious={previous ? () => setReviewing(previous.rule_id) : undefined}
          onNext={next ? () => setReviewing(next.rule_id) : undefined}
          onBack={() => setReviewing(null)}
          onSaved={(ruleId, compliance, toNext) => {
            setSaved((was) => ({ ...was, [ruleId]: compliance }))
            if (toNext && next) setReviewing(next.rule_id)
          }}
        />
      </section>
    )
  }

  async function exportAs(format: 'csv' | 'xlsx') {
    setExporting(true)
    setError(null)
    try {
      await api.downloadRun(detail.id, format)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="run-verdicts" data-testid="run-verdicts">
      <div className="evaluations-head">
        <h4>
          {detail.portfolio_code}’s verdicts on holdings as at {day}
        </h4>
        <div className="actions">
          <RowMenu
            label="Export"
            text="Export"
            disabled={exporting}
            items={[
              { label: 'CSV', onSelect: () => void exportAs('csv') },
              { label: 'Excel', onSelect: () => void exportAs('xlsx') },
            ]}
          />
          {onClose && (
            <button type="button" className="ghost" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {/* What the fund is, beside what was found in it: its benchmark, base currency
          and hedging, as recorded when the run was made. */}
      {(detail.facts ?? []).length > 0 && (
        <p className="fund-facts" data-testid="run-facts">
          {detail.facts!.map((f) => (
            <span key={f.name} title={f.source_anchor ? `From cell ${f.source_anchor}` : undefined}>
              {f.label}: <strong>{f.value}</strong>
            </span>
          ))}
        </p>
      )}
      <p className="note">
        Ran {new Date(detail.ran_at).toLocaleString()}, against {detail.portfolio_code}’s limits in
        force on {asAt(detail.rules_as_of)} as they stood at that moment. Warn is within{' '}
        {Math.round(detail.warn_tolerance * 100)}% of a limit, or {detail.notch_tolerance} notch
        {detail.notch_tolerance === 1 ? '' : 'es'} of a rating floor.
        {detail.policies_applied.length > 0 &&
          ` Ratings read through ${detail.policies_applied.map((key) => rowName(key).toLowerCase()).join(' and ')}.`}
      </p>

      {judged.length === 0 ? (
        <p className="empty">None of this fund’s limits is a number or a rating to judge.</p>
      ) : (
        <div className="table-scroll">
          <table className="grid stacked" data-testid="verdicts">
            <thead>
              <tr>
                <th>Verdict</th>
                <th>Limit</th>
                <th>Covers</th>
                <th>Found</th>
                <th>Headroom</th>
                <th>Why</th>
                {reviewed && <th>Compliance</th>}
                {reviewed && <th />}
              </tr>
            </thead>
            <tbody>
              {judged.map((v) => (
                <tr key={v.rule_id} data-testid="verdict">
                  <td>
                    <span className={`verdict-count ${v.state}`}>{STATE_LABEL[v.state]}</span>
                    <span className="clause verdict-as-at" data-testid="verdict-as-at">
                      as at {day}
                    </span>
                  </td>
                  <td>
                    <strong>{rowName(v.rule_key)}</strong>
                    <span className="clause">
                      {says({ operator: v.operator, threshold: v.threshold, measure: v.measure, template: v.template }).text}
                    </span>
                    {v.benchmark && (
                      <span className="clause" data-testid="verdict-benchmark">
                        {benchmarkSays(v)}
                      </span>
                    )}
                  </td>
                  <td>{covers(v)}</td>
                  <td>
                    {amount(v.value, v.unit)}
                    {v.groups.length > 0 && (
                      <ul className="verdict-groups">
                        {v.groups.slice(0, SHOWN_GROUPS).map((g) => (
                          <li key={g.key ?? 'all'}>
                            <span className={`verdict-dot ${g.state}`} aria-hidden />
                            {g.key ?? 'All'}: {amount(g.value, v.unit)}
                            {g.benchmark !== null && g.benchmark !== undefined && ` (benchmark ${amount(g.benchmark, v.unit)})`}
                          </li>
                        ))}
                        {v.groups.length > SHOWN_GROUPS && (
                          <li>and {v.groups.length - SHOWN_GROUPS} more</li>
                        )}
                      </ul>
                    )}
                  </td>
                  <td>{room(v)}</td>
                  <td>{why(v, detail)}</td>
                  {reviewed && (
                    <td>
                      <ComplianceCell compliance={complianceOf(v)} />
                    </td>
                  )}
                  {reviewed && <td className="run-actions">{reviewMenu(v)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Wording no holding is checked against, laid out to be read against the
          holdings: what each says, and the cell it came from. */}
      {words.length > 0 && (
        <section className="verdict-words" data-testid="text-only-limits">
          <h4>
            {words.length} limit{words.length === 1 ? '' : 's'} written as words, read by a person
          </h4>
          <p className="note">
            Each limit’s words are looked for among this snapshot’s issuers, securities and
            classifications. What is found is for you to read against the wording, not a verdict.
          </p>
          <div className="table-scroll">
            <table className="grid stacked" data-testid="text-limits">
              <thead>
                <tr>
                  <th>Limit</th>
                  <th>Wording</th>
                  <th>Found in this snapshot</th>
                  {reviewed && <th>Compliance</th>}
                  {reviewed && <th />}
                </tr>
              </thead>
              <tbody>
                {words.map((v) => (
                  <tr key={v.rule_id}>
                    <td>
                      <strong>{rowName(v.rule_key)}</strong>
                      {v.source_anchor && <span className="clause">Cell {v.source_anchor}</span>}
                    </td>
                    <td>{v.narrative_text ?? ''}</td>
                    <td>
                      <Found review={v.review} />
                    </td>
                    {reviewed && (
                      <td>
                        <ComplianceCell compliance={complianceOf(v)} />
                      </td>
                    )}
                    {reviewed && <td className="run-actions">{reviewMenu(v)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  )
}
